import { EarningsItemBase, FinancialData } from '@api';
import { SmartTableColumn, WithNAFallback } from '@design';
import { formatPercentage, getCurrencySymbol } from '@helpers';
import { formatNumberWithUnit } from '@terminal/widgets/earnings/utils/utils';
import { SharedTable } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/shared-table/SharedTable';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './EarningsExpectationsTable.module.scss';

export interface EarningsExpectationsTableProps {
  earningsData: EarningsItemBase;
}

export const EarningsExpectationsTable = ({
  earningsData,
}: EarningsExpectationsTableProps) => {
  const { t } = useTranslation(['earnings']);
  const { eps, revenue } = earningsData;

  const naContainerPadding = '10px 12px';

  const tableData = [
    {
      name: t('earnings:earningsInsight.drawer.earningsPreview.tables.eps'),
      key: 'eps',
      ...eps,
    },
    {
      name: t('earnings:earningsInsight.drawer.earningsPreview.tables.revenue'),
      key: 'revenue',
      ...revenue,
    },
  ];

  const tableColumns: SmartTableColumn<
    FinancialData & { name: string; key: string }
  >[] = [
    {
      label: 'Name',
      key: 'name',
      renderHeader: () => null,
      render: value => <div className={styles.nameCell}>{value}</div>,
    },
    {
      label: t(
        'earnings:earningsInsight.drawer.earningsPreview.tables.estimate'
      ),
      key: 'estimate',
      alignment: 'right',
      render: value => {
        const currencySymbol = getCurrencySymbol(earningsData.currency);
        return (
          <WithNAFallback
            containerPadding={naContainerPadding}
            check={value !== undefined && value !== null}
            value={
              <div
                className={styles.numberCell}
              >{`${currencySymbol}${formatNumberWithUnit(value)}`}</div>
            }
          />
        );
      },
    },
    {
      label: t('earnings:earningsInsight.drawer.earningsPreview.tables.yoy'),
      key: 'estimate_yoy_percentage',
      alignment: 'right',
      render: value => (
        <WithNAFallback
          containerPadding={naContainerPadding}
          value={
            value !== undefined && (
              <div
                className={clsx(styles.yoyCell, {
                  [styles.yoyNeutral]: value === 0,
                  [styles.yoyPositive]: value > 0,
                  [styles.yoyNegative]: value < 0,
                })}
              >
                {formatPercentage(value, {
                  suffix: '%',
                  addPlus: true,
                  useOriginalValue: true,
                })}
              </div>
            )
          }
        />
      ),
    },
  ];

  return (
    <SharedTable
      title={t(
        'earnings:earningsInsight.drawer.earningsPreview.earningsExpectations'
      )}
      tableData={tableData}
      tableColumns={tableColumns}
      noCellPadding
    />
  );
};
